import IUser from "@/interfaces/IUser";

/**
 * Prénom + nom de l'interlocuteur, concaténés et nettoyés.
 * Retourne null si aucun des deux n'est renseigné.
 */
export function formatInterlocutor(
  firstname?: string | null,
  lastname?: string | null
): string | null {
  const full = [firstname, lastname].filter(Boolean).join(" ").trim();
  return full.length > 0 ? full : null;
}

interface CompanyContactSource {
  interlocutorFirstname?: string | null;
  interlocutorLastname?: string | null;
  owner?: { fullname?: string | null } | null;
}

/**
 * Qui appeler au sujet d'une formation, et à quel titre.
 *
 * L'interlocuteur d'une formation est toujours celui de l'entreprise : la
 * formation n'en porte plus (l'ancienne colonne `interlocutor` était
 * pré-remplie avec le gérant et n'est plus lue). À défaut on prend le gérant.
 * Le libellé suit la source : sans lui on ne saurait pas si l'on s'apprête à
 * appeler le contact du dossier ou le patron.
 */
export function resolveCourseContact(
  company?: CompanyContactSource | null
): { label: "Interlocuteur" | "Gérant"; name: string } | null {
  const companyInterlocutor = formatInterlocutor(
    company?.interlocutorFirstname,
    company?.interlocutorLastname
  );
  if (companyInterlocutor) {
    return { label: "Interlocuteur", name: companyInterlocutor };
  }

  const owner = company?.owner?.fullname?.trim();
  return owner ? { label: "Gérant", name: owner } : null;
}

/**
 * Onglet sur lequel s'ouvre une fiche entreprise, faute de `?tab=` dans l'URL.
 *
 * Un CSM entre par la formation, c'est son écran de travail. Tout le monde
 * d'autre entre par les notes : commercial, télépro, et l'administrateur aussi.
 *
 * Le test porte sur le rôle CSM seul, volontairement : `checkIfUserHasRoles`
 * laisse passer un ADMIN sur n'importe quelle vérification (CSM == ADMIN dans
 * le reste du CRM), et l'utiliser ici enverrait les admins sur Formation.
 */
export function defaultCompanyTab(user?: IUser | null): "courses" | "notes" {
  return user?.roles?.includes("CSM") ? "courses" : "notes";
}
